import React from 'react';
import { Check, X, Send, Undo2 } from 'lucide-react';

interface Item {
  id: string;
  text: string;
}

export interface PairLink {
  leftId: string;
  rightId: string;
}

interface Props {
  leftItems: Item[];
  rightItems: Item[];
  links: PairLink[];
  onLink: (leftId: string, rightId: string) => void;
  onUnlink: (leftId: string) => void;
  onSubmit: () => void;
  submitted?: boolean;
  disabled?: boolean;
  /** Tras el cierre: la correspondencia correcta, que llega del servidor. */
  correctPairs?: PairLink[];
  /** Sin botón de confirmar: en los exámenes la respuesta se guarda sola al cambiarla. */
  hideSubmit?: boolean;
}

/**
 * Emparejar columnas.
 *
 * Se resuelve tocando: primero un elemento de la izquierda, después su pareja
 * de la derecha. Nada de arrastrar — en un móvil, arrastrar contra reloj es
 * una fuente de errores que no mide conocimiento, solo pulso.
 */
export const MatchPairsQuestion: React.FC<Props> = ({
  leftItems,
  rightItems,
  links,
  onLink,
  onUnlink,
  onSubmit,
  submitted = false,
  disabled = false,
  correctPairs,
  hideSubmit = false,
}) => {
  const [activeLeft, setActiveLeft] = React.useState<string | null>(null);
  const revealed = Array.isArray(correctPairs);
  const locked = disabled || submitted || revealed;

  const linkByLeft = new Map(links.map((l) => [l.leftId, l.rightId]));
  const linkedRightIds = new Set(links.map((l) => l.rightId));

  // Índice de emparejado, para pintar el mismo número en ambas columnas.
  const orderOfLeft = new Map(leftItems.map((item, i) => [item.id, i + 1]));

  const isLinkCorrect = (leftId: string): boolean | null => {
    if (!revealed) return null;
    const chosen = linkByLeft.get(leftId);
    if (!chosen) return false;
    return correctPairs!.some((p) => p.leftId === leftId && p.rightId === chosen);
  };

  const handleRightClick = (rightId: string) => {
    if (locked) return;
    if (linkedRightIds.has(rightId)) {
      // Tocar una ficha ya emparejada la libera.
      const owner = links.find((l) => l.rightId === rightId);
      if (owner) onUnlink(owner.leftId);
      return;
    }
    if (activeLeft) {
      onLink(activeLeft, rightId);
      setActiveLeft(null);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto space-y-4">
      <p className="text-center text-ink-400 text-sm">
        Toca un elemento de la izquierda y después su pareja de la derecha
      </p>

      <div className="grid grid-cols-2 gap-3 md:gap-6">
        {/* Columna izquierda */}
        <div className="space-y-2.5">
          {leftItems.map((item) => {
            const linked = linkByLeft.has(item.id);
            const correct = isLinkCorrect(item.id);
            const isActive = activeLeft === item.id;

            let tone = 'bg-ink-850 border-ink-700';
            if (revealed) {
              tone = correct
                ? 'bg-success-500/15 border-success-500'
                : 'bg-danger-500/12 border-danger-500';
            } else if (isActive) {
              tone = 'bg-sky-600 border-sky-400 ring-2 ring-sky-300';
            } else if (linked) {
              tone = 'bg-ink-800 border-sky-700';
            }

            return (
              <button
                key={item.id}
                type="button"
                disabled={locked}
                onClick={() => {
                  if (locked) return;
                  if (linked) onUnlink(item.id);
                  else setActiveLeft(isActive ? null : item.id);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 text-left transition-all ${tone} ${
                  locked ? 'cursor-default' : 'cursor-pointer'
                }`}
              >
                <span
                  className={`w-7 h-7 rounded-lg shrink-0 flex items-center justify-center text-xs font-bold ${
                    linked ? 'bg-sky-500 text-white' : 'bg-ink-700 text-ink-400'
                  }`}
                >
                  {linked ? orderOfLeft.get(item.id) : '–'}
                </span>
                <span className="flex-1 text-ink-50 font-medium text-sm md:text-base">
                  {item.text}
                </span>
                {revealed &&
                  (correct ? (
                    <Check className="w-5 h-5 text-success-400 shrink-0" />
                  ) : (
                    <X className="w-5 h-5 text-danger-400 shrink-0" />
                  ))}
                {!revealed && linked && <Undo2 className="w-4 h-4 text-ink-500 shrink-0" />}
              </button>
            );
          })}
        </div>

        {/* Columna derecha */}
        <div className="space-y-2.5">
          {rightItems.map((item) => {
            const owner = links.find((l) => l.rightId === item.id);
            const linked = Boolean(owner);
            const correct = owner ? isLinkCorrect(owner.leftId) : null;

            let tone = 'bg-ink-850 border-ink-700';
            if (revealed && linked) {
              tone = correct
                ? 'bg-success-500/15 border-success-500'
                : 'bg-danger-500/12 border-danger-500';
            } else if (linked) {
              tone = 'bg-ink-800 border-sky-700';
            } else if (activeLeft) {
              // Mientras haya un elemento activo, las fichas libres se destacan.
              tone = 'bg-ink-800 border-ember-500/60 hover:border-ember-400';
            }

            return (
              <button
                key={item.id}
                type="button"
                disabled={locked}
                onClick={() => handleRightClick(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 text-left transition-all ${tone} ${
                  locked ? 'cursor-default' : 'cursor-pointer'
                }`}
              >
                <span
                  className={`w-7 h-7 rounded-lg shrink-0 flex items-center justify-center text-xs font-bold ${
                    linked ? 'bg-sky-500 text-white' : 'bg-ink-700 text-ink-400'
                  }`}
                >
                  {linked && owner ? orderOfLeft.get(owner.leftId) : '–'}
                </span>
                <span className="flex-1 text-ink-50 font-medium text-sm md:text-base">
                  {item.text}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {!revealed && !hideSubmit && (
        <button
          type="button"
          onClick={onSubmit}
          disabled={locked || links.length === 0}
          className="
            w-full flex items-center justify-center gap-2 py-4 rounded-xl font-bold
            bg-ember-500 hover:bg-ember-400 text-on-accent
            disabled:bg-ink-800 disabled:text-ink-500 disabled:cursor-not-allowed
            transition-colors
          "
        >
          <Send className="w-5 h-5" />
          {submitted
            ? 'Respuesta enviada'
            : `Confirmar ${links.length} de ${leftItems.length}`}
        </button>
      )}

      {revealed && (
        <p className="text-center text-ink-400 text-sm">
          Aciertos: {leftItems.filter((i) => isLinkCorrect(i.id)).length} de {leftItems.length}
          {' · se puntúa por pareja acertada'}
        </p>
      )}
    </div>
  );
};
